import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import type { ParteAr, SegnoAr } from '../../types/ApparecchiArTypes';

/**
 * Il confronto fra modelli che propongono dove stanno le parti.
 *
 * ⚠️ **Si guarda un concorrente alla volta, non tutti insieme.** Sul tavolo il colore
 * significa già da dove viene un punto — verde posato a mano, azzurro letto dalla
 * serigrafia, viola bozza: dare tre colori nuovi ai tre modelli renderebbe illeggibile
 * proprio la distinzione che conta di più. Alternarli è anche il modo in cui si
 * confrontano davvero due sovrapposizioni: si accende una, si accende l'altra, e lo
 * scarto si vede da sé.
 *
 * ⚠️ **Accettare non è confermare.** I punti presi da un modello entrano come bozze
 * tratteggiate: restano da guardare uno per uno, e diventano dati solo quando qualcuno
 * li sposta o li conferma. Su un apparecchio in corrente continua è l'unica prudenza
 * che vale la pena costare due clic in più.
 */

export interface Concorrente {
  modello: string;
  etichetta: string;
  punti: Array<{ parte: string; x: number; y: number; fiducia?: number; motivo?: string }>;
  millisecondi: number;
  token: { input: number; output: number };
  errore?: string;
}

interface Props {
  apparecchioId: string;
  superficieId: string;
  parti: ParteAr[];
  /** Le parti già segnate qui: un modello che le ripropone non aggiunge niente. */
  giaSegnate: string[];
  onAnteprima: (punti: SegnoAr[] | null) => void;
  onAccetta: (punti: SegnoAr[]) => void;
}

export default function ConfrontoModelli({
  apparecchioId,
  superficieId,
  parti,
  giaSegnate,
  onAnteprima,
  onAccetta,
}: Props) {
  const [inCorso, setInCorso] = useState(false);
  const [concorrenti, setConcorrenti] = useState<Concorrente[] | null>(null);
  const [sceltoId, setSceltoId] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const chiedi = async (conConfronto = false) => {
    setInCorso(true);
    setErrore(null);
    try {
      const fn = httpsCallable<
        { apparecchioId: string; superficieId: string; confronto?: boolean },
        { concorrenti: Concorrente[] }
      >(functions, 'proponiPuntiAr');
      const esito = await fn({ apparecchioId, superficieId, confronto: conConfronto });
      setConcorrenti(esito.data.concorrenti);
      const primo = esito.data.concorrenti.find((c) => !c.errore && c.punti.length > 0);
      if (primo) {
        setSceltoId(primo.modello);
        onAnteprima(inBozze(primo, giaSegnate));
      }
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'la richiesta non è riuscita');
    } finally {
      setInCorso(false);
    }
  };

  const mostra = (c: Concorrente) => {
    setSceltoId(c.modello);
    onAnteprima(inBozze(c, giaSegnate));
  };

  const scelto = concorrenti?.find((c) => c.modello === sceltoId) ?? null;
  const daAccettare = scelto ? inBozze(scelto, giaSegnate) : [];

  if (!concorrenti) {
    return (
      <Box sx={{ mt: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={inCorso ? <CircularProgress size={14} /> : <AutoAwesome />}
            onClick={() => void chiedi(false)}
            disabled={inCorso}
            variant="outlined"
          >
            {inCorso ? 'Sto guardando la fotografia…' : 'Proponi le posizioni'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Un modello guarda questa fotografia e propone dove stanno le parti. Non salva
            niente: quello che torna sono bozze.
          </Typography>
        </Stack>
        {/*
          ⚠️ Il confronto a tre resta, ma si chiede. Misurato il 5 agosto 2026 su cinque
          giri: fra Robotics-ER 2 e Gemini 3.1 Pro lo scarto dal riferimento umano è 89
          px contro 90 — rumore — e Robotics costa cinque volte meno; Claude si sposta da
          solo di 179 px contro 18, a settantaquattro volte il prezzo. Chiamarli tutti a
          ogni proposta è pagare tre risposte per averne una.
        */}
        <Button
          size="small"
          onClick={() => void chiedi(true)}
          disabled={inCorso}
          sx={{ mt: 0.5, textTransform: 'none', color: 'text.secondary', px: 0.5 }}
        >
          oppure confronta tre modelli — più lento e più caro, per un apparecchio difficile
        </Button>
        {errore && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errore}
          </Alert>
        )}
      </Box>
    );
  }

  const unoSolo = concorrenti.length === 1;

  return (
    <Box sx={{ mt: 1.5 }}>
      {/*
        ⚠️ Con un concorrente solo le schede di scelta non hanno niente da scegliere:
        diventano un riquadro da leggere prima di poter fare la cosa che si voleva fare.
        Restano quando i modelli sono più d'uno.
      */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, display: unoSolo ? 'none' : 'flex' }}>
        {concorrenti.map((c) => {
          const attivo = c.modello === sceltoId;
          const nuovi = inBozze(c, giaSegnate).length;
          return (
            <Box
              key={c.modello}
              onClick={() => !c.errore && mostra(c)}
              sx={{
                flex: '1 1 200px',
                minWidth: 200,
                p: 1.25,
                border: '1px solid',
                borderColor: attivo ? 'primary.main' : 'divider',
                bgcolor: attivo ? 'action.selected' : 'transparent',
                borderRadius: 1.5,
                cursor: c.errore ? 'not-allowed' : 'pointer',
                opacity: c.errore ? 0.55 : 1,
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: 13 }} noWrap>
                {c.etichetta}
              </Typography>
              {c.errore ? (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                  {c.errore}
                </Typography>
              ) : (
                <>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={`${c.punti.length} punti`}
                      sx={{ height: 19, fontSize: 11 }}
                    />
                    {nuovi !== c.punti.length && (
                      <Chip
                        size="small"
                        label={`${nuovi} nuovi`}
                        sx={{ height: 19, fontSize: 11 }}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  {/*
                    ⚠️ Il costo si mostra sempre, anche quando è piccolo: una chiamata a
                    consumo che non compare da nessuna parte diventa un margine
                    immaginario, e ci si accorge solo alla fattura.
                  */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 0.25,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {(c.millisecondi / 1000).toFixed(1)} s · {c.token.input.toLocaleString('it-IT')} in
                    {' · '}
                    {c.token.output.toLocaleString('it-IT')} out
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Stack>

      {unoSolo && scelto && !scelto.errore && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {scelto.etichetta} · {scelto.punti.length} parti trovate ·{' '}
          {(scelto.millisecondi / 1000).toFixed(1)} s ·{' '}
          {(scelto.token.input + scelto.token.output).toLocaleString('it-IT')} token
        </Typography>
      )}
      {unoSolo && scelto?.errore && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {scelto.etichetta}: {scelto.errore}
        </Alert>
      )}

      {scelto && !scelto.errore && (
        <Box sx={{ mt: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
            <Button
              size="small"
              variant="contained"
              disabled={daAccettare.length === 0}
              onClick={() => {
                onAccetta(daAccettare);
                onAnteprima(null);
                setConcorrenti(null);
                setSceltoId(null);
              }}
            >
              Prendi questi {daAccettare.length} come bozze
            </Button>
            <Button
              size="small"
              onClick={() => {
                onAnteprima(null);
                setConcorrenti(null);
                setSceltoId(null);
              }}
            >
              Scarta tutto
            </Button>
            <Typography variant="caption" color="text.secondary">
              Entrano tratteggiate: restano da guardare una per una.
            </Typography>
          </Stack>

          {/*
            ⚠️ Il MOTIVO di ogni punto si legge qui, non sul tavolo. È il dato che
            distingue un modello che ha guardato il connettore da uno che ha centrato la
            scritta accanto — e senza leggerlo i tre concorrenti si somigliano tutti.
          */}
          <Box sx={{ maxHeight: 150, overflowY: 'auto', pr: 1 }}>
            {scelto.punti.map((p) => {
              const parte = parti.find((x) => x.id === p.parte);
              const gia = giaSegnate.includes(p.parte);
              return (
                <Stack
                  key={p.parte}
                  direction="row"
                  spacing={1}
                  alignItems="baseline"
                  sx={{ py: 0.35, opacity: gia ? 0.45 : 1 }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 11.5,
                      fontWeight: 600,
                      minWidth: 92,
                    }}
                    noWrap
                  >
                    {parte?.sigla?.trim() || parte?.nome || p.parte}
                  </Typography>
                  {typeof p.fiducia === 'number' && (
                    <Tooltip title="quanto il modello dichiara di esserne sicuro">
                      <Typography
                        variant="caption"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          color: p.fiducia >= 0.8 ? 'success.main' : 'warning.main',
                          minWidth: 34,
                        }}
                      >
                        {Math.round(p.fiducia * 100)}%
                      </Typography>
                    </Tooltip>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    {gia ? 'già segnata a mano — non verrà toccata' : p.motivo}
                  </Typography>
                </Stack>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/**
 * Da proposte a bozze.
 *
 * ⚠️ **Le parti già segnate a mano non si toccano mai.** Chi le ha messe le ha guardate;
 * sovrascriverle con una proposta significherebbe degradare un dato verificato in una
 * bozza, e nessuno se ne accorgerebbe finché un installatore non trova il connettore
 * sbagliato.
 */
function inBozze(c: Concorrente, giaSegnate: string[]): SegnoAr[] {
  return c.punti
    .filter((p) => !giaSegnate.includes(p.parte))
    .map((p) => ({ parte: p.parte, x: p.x, y: p.y, r: 0, origine: 'bozza_ia' as const }));
}
